import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  FileCheck,
  Upload,
  User,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { api, formatApiErrorDetail } from "../api";
import { LOGOS } from "../../lib/ablemp-data";
import { formatCpfCnpj, formatPhone } from "../../lib/masks";

const PF_DOCS = [
  {
    id: "documento_identificacao",
    title: "Documento de identificação",
    hint: "RG, CNH ou Passaporte (frente e verso em um único PDF, se possível).",
  },
  {
    id: "comprovante_endereco",
    title: "Comprovante de endereço",
    hint: "Conta de luz, água, telefone ou similar — emitido nos últimos 90 dias.",
  },
];

const PJ_DOCS = [
  {
    id: "contrato_social",
    title: "Contrato Social",
    hint: "Última versão consolidada / Estatuto registrado.",
  },
  {
    id: "documento_representante",
    title: "Documento do representante legal",
    hint: "RG, CNH ou Passaporte do administrador / sócio responsável.",
  },
  {
    id: "comprovante_endereco",
    title: "Comprovante de endereço",
    hint: "Comprovante da sede da empresa — emitido nos últimos 90 dias.",
  },
];

const TIPOS_FALLBACK = [
  { name: "Fundador", monthly_fee: 0, annual_fee: 0 },
  { name: "Contribuinte Titular", monthly_fee: 500, annual_fee: 6000 },
  { name: "Contribuinte", monthly_fee: 150, annual_fee: 1800 },
  { name: "Colaborador", monthly_fee: 50, annual_fee: 600 },
  { name: "Honorário", monthly_fee: 0, annual_fee: 0 },
];

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export default function Cadastro() {
  const { register, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [personType, setPersonType] = useState(null);
  const [tipos, setTipos] = useState(TIPOS_FALLBACK);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    phone: "",
    cpf_cnpj: "",
    tipo: "Contribuinte",
    address: "",
    boleto_email: "",
    financial_contact_name: "",
    financial_contact_email: "",
    financial_contact_phone: "",
    legal_rep_name: "",
    legal_rep_email: "",
    legal_rep_phone: "",
  });
  const [uploading, setUploading] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    api
      .get("/public/associate-types")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setTipos(r.data);
      })
      .catch(() => {});
  }, []);

  const selectedTipo = tipos.find((t) => t.name === form.tipo);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const requiredDocs = personType === "PJ" ? PJ_DOCS : PF_DOCS;

  // ---------- Step 1: PF / PJ ----------
  const Step1 = (
    <div data-testid="step-1">
      <StepBadge step={1} total={3} label="Tipo de pessoa" />
      <h1 className="mt-2 font-serif-display text-4xl lg:text-5xl leading-tight">
        Você é{" "}
        <span className="italic font-light text-[#002776]">Pessoa Física</span>{" "}
        ou Jurídica?
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Essa escolha define quais documentos serão solicitados para sua
        avaliação junto à ABLEMP.
      </p>
      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <TypeCard
          testid="select-pf"
          icon={User}
          title="Pessoa Física"
          hint="Documento de identificação + comprovante de endereço."
          onClick={() => {
            setPersonType("PF");
            setStep(2);
          }}
          accent="#002776"
        />
        <TypeCard
          testid="select-pj"
          icon={Building2}
          title="Pessoa Jurídica"
          hint="Contrato Social + documento do representante legal + comprovante de endereço."
          onClick={() => {
            setPersonType("PJ");
            setStep(2);
          }}
          accent="#009B3A"
        />
      </div>
    </div>
  );

  // ---------- Step 2: data + tipo de associado ----------
  const submitData = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Senha deve ter ao menos 8 caracteres");
      return;
    }
    if (form.password !== form.confirm) {
      setError("As senhas não conferem");
      return;
    }
    setSubmitting(true);
    const res = await register({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      phone: form.phone || null,
      cpf_cnpj: form.cpf_cnpj || null,
      person_type: personType,
      tipo: form.tipo,
      address: form.address || null,
      boleto_email: form.boleto_email || null,
      financial_contact_name: form.financial_contact_name || null,
      financial_contact_email: form.financial_contact_email || null,
      financial_contact_phone: form.financial_contact_phone || null,
      legal_rep_name: form.legal_rep_name || null,
      legal_rep_email: form.legal_rep_email || null,
      legal_rep_phone: form.legal_rep_phone || null,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    setRegistered(true);
    setStep(3);
    toast.success("Dados salvos! Agora envie os documentos.");
  };

  const Step2 = (
    <div data-testid="step-2">
      <StepBadge
        step={2}
        total={3}
        label={`Dados ${personType === "PJ" ? "(Pessoa Jurídica)" : "(Pessoa Física)"}`}
      />
      <h1 className="mt-2 font-serif-display text-4xl lg:text-5xl leading-tight">
        Seus dados de cadastro
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Informe seus dados e o tipo de associação desejado. Após o envio dos
        documentos, sua solicitação será analisada pela administração.
      </p>

      <form
        data-testid="signup-form"
        onSubmit={submitData}
        className="mt-8 grid md:grid-cols-2 gap-4"
      >
        <div className="md:col-span-2">
          <Label>{personType === "PJ" ? "Razão Social" : "Nome completo"} *</Label>
          <Input
            testid="signup-name"
            required
            minLength={2}
            value={form.name}
            onChange={upd("name")}
            placeholder={personType === "PJ" ? "Razão social da empresa" : "Seu nome completo"}
          />
        </div>
        <div>
          <Label>E-mail *</Label>
          <Input
            testid="signup-email"
            type="email"
            required
            value={form.email}
            onChange={upd("email")}
            placeholder="contato@empresa.com.br"
          />
        </div>
        <div>
          <Label>Telefone</Label>
          <Input
            testid="signup-phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
            placeholder="(11) 99999-9999"
            maxLength={15}
          />
        </div>
        <div>
          <Label>{personType === "PJ" ? "CNPJ" : "CPF"}</Label>
          <Input
            testid="signup-doc"
            value={form.cpf_cnpj}
            onChange={(e) =>
              setForm((f) => ({ ...f, cpf_cnpj: formatCpfCnpj(e.target.value, personType) }))
            }
            placeholder={personType === "PJ" ? "XX.XXX.XXX/XXXX-XX" : "XXX.XXX.XXX-XX"}
            maxLength={personType === "PJ" ? 18 : 14}
          />
        </div>
        <div>
          <Label>Tipo de Associado *</Label>
          <select
            data-testid="signup-tipo"
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
          >
            {tipos.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
                {t.monthly_fee > 0
                  ? ` — ${formatBRL(t.monthly_fee)}/mês`
                  : t.annual_fee > 0
                  ? ` — ${formatBRL(t.annual_fee)}/ano`
                  : " — isento"}
              </option>
            ))}
          </select>
          {selectedTipo && (
            <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600 space-y-0.5">
              {selectedTipo.description && (
                <div>{selectedTipo.description}</div>
              )}
              <div className="flex gap-4 text-slate-700 mt-1">
                {selectedTipo.monthly_fee > 0 && (
                  <span>
                    <strong>Mensal:</strong>{" "}
                    {formatBRL(selectedTipo.monthly_fee)}
                  </span>
                )}
                {selectedTipo.annual_fee > 0 && (
                  <span>
                    <strong>Anual:</strong>{" "}
                    {formatBRL(selectedTipo.annual_fee)}
                  </span>
                )}
                {selectedTipo.monthly_fee === 0 &&
                  selectedTipo.annual_fee === 0 && (
                    <span className="text-emerald-700">Isento de contribuição</span>
                  )}
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <Label>Endereço</Label>
          <Input
            testid="signup-address"
            value={form.address}
            onChange={upd("address")}
            placeholder="Rua, número, bairro, cidade — UF, CEP"
          />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#002776]">
            Cobrança e Departamento Financeiro
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Boletos e recibos serão enviados para o e-mail principal acima e
            também para os contatos informados abaixo.
          </p>
        </div>
        <div className="md:col-span-2">
          <Label>E-mail para envio de Boletos</Label>
          <Input
            testid="signup-boleto-email"
            type="email"
            value={form.boleto_email}
            onChange={upd("boleto_email")}
            placeholder="boletos@empresa.com.br"
          />
        </div>
        <div>
          <Label>Nome do Contato — Depto Financeiro</Label>
          <Input
            testid="signup-fin-name"
            value={form.financial_contact_name}
            onChange={upd("financial_contact_name")}
            placeholder="Ex.: Maria Souza"
          />
        </div>
        <div>
          <Label>Telefone do Financeiro</Label>
          <Input
            testid="signup-fin-phone"
            value={form.financial_contact_phone}
            onChange={(e) =>
              setForm((f) => ({ ...f, financial_contact_phone: formatPhone(e.target.value) }))
            }
            placeholder="(11) 99999-9999"
            maxLength={15}
          />
        </div>
        <div className="md:col-span-2">
          <Label>E-mail do Financeiro</Label>
          <Input
            testid="signup-fin-email"
            type="email"
            value={form.financial_contact_email}
            onChange={upd("financial_contact_email")}
            placeholder="financeiro@empresa.com.br"
          />
        </div>

        {personType === "PJ" && (
          <>
            <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#002776]">
                Representante Legal
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Obrigatório apenas para Pessoa Jurídica.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label>Nome do Representante Legal *</Label>
              <Input
                testid="signup-lr-name"
                value={form.legal_rep_name}
                onChange={upd("legal_rep_name")}
                placeholder="Nome completo"
              />
            </div>
            <div>
              <Label>E-mail do Representante Legal *</Label>
              <Input
                testid="signup-lr-email"
                type="email"
                value={form.legal_rep_email}
                onChange={upd("legal_rep_email")}
              />
            </div>
            <div>
              <Label>Telefone do Representante Legal *</Label>
              <Input
                testid="signup-lr-phone"
                value={form.legal_rep_phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, legal_rep_phone: formatPhone(e.target.value) }))
                }
                placeholder="(11) 99999-9999"
                maxLength={15}
              />
            </div>
          </>
        )}

        <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Acesso ao Portal
          </div>
        </div>
        <div>
          <Label>Senha *</Label>
          <Input
            testid="signup-password"
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={upd("password")}
            placeholder="Mínimo 8 caracteres"
          />
        </div>
        <div>
          <Label>Confirmar senha *</Label>
          <Input
            testid="signup-confirm"
            type="password"
            required
            minLength={8}
            value={form.confirm}
            onChange={upd("confirm")}
            placeholder="Repita a senha"
          />
        </div>

        {error && (
          <div
            data-testid="signup-error"
            className="md:col-span-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-sm"
          >
            {error}
          </div>
        )}

        <div className="md:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Voltar
          </button>
          <button
            data-testid="signup-submit"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-7 py-3 text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#002776] transition-colors disabled:opacity-60"
          >
            {submitting ? "Enviando..." : "Avançar para documentos"}
            <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </div>
  );

  // ---------- Step 3: uploads ----------
  const handleUpload = async (docId, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15MB");
      return;
    }
    const fd = new FormData();
    fd.append("doc_type", docId);
    fd.append("file", file);
    setUploading((u) => ({ ...u, [docId]: true }));
    try {
      const res = await api.post("/portal/onboarding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded((u) => ({ ...u, [docId]: res.data }));
      toast.success("Documento enviado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading((u) => ({ ...u, [docId]: false }));
    }
  };

  const allDone = requiredDocs.every((d) => uploaded[d.id]?.file_name);

  const finalize = async () => {
    if (!allDone) return;
    setFinalizing(true);
    try {
      await api.post("/portal/onboarding/complete", {
        person_type: personType,
      });
      toast.success("Solicitação enviada para avaliação!");
      logout();
      navigate("/aguardando-aprovacao?submitted=1", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setFinalizing(false);
    }
  };

  const Step3 = (
    <div data-testid="step-3">
      <StepBadge step={3} total={3} label="Documentos" />
      <h1 className="mt-2 font-serif-display text-4xl lg:text-5xl leading-tight">
        Envie seus documentos
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Documentos para análise da administração da ABLEMP. Aceitamos PDF, JPG
        ou PNG (até 15MB por arquivo).
      </p>

      <ul className="mt-8 space-y-4">
        {requiredDocs.map((d) => {
          const ud = uploaded[d.id];
          const done = !!ud?.file_name;
          const busy = uploading[d.id];
          return (
            <li
              key={d.id}
              data-testid={`doc-row-${d.id}`}
              className={`bg-white border rounded-md p-5 transition-colors ${
                done ? "border-emerald-200" : "border-slate-200"
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`h-12 w-12 grid place-items-center rounded-sm shrink-0 ${
                    done
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {done ? (
                    <CheckCircle2 size={22} strokeWidth={1.6} />
                  ) : (
                    <FileCheck size={22} strokeWidth={1.4} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-xl leading-tight">
                    {d.title}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{d.hint}</p>
                  {done && (
                    <div className="mt-2 text-xs text-emerald-700">
                      Enviado: {ud.file_name}
                    </div>
                  )}
                </div>
                <label
                  className={`inline-flex items-center gap-2 px-4 py-2.5 cursor-pointer text-[11px] uppercase tracking-[0.22em] ${
                    done
                      ? "border border-slate-300 hover:border-[#002776] text-slate-700"
                      : "bg-[#0A0F14] text-white hover:bg-[#002776]"
                  }`}
                >
                  <Upload size={14} />
                  {busy ? "Enviando..." : done ? "Substituir" : "Enviar"}
                  <input
                    data-testid={`upload-${d.id}`}
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    disabled={busy}
                    onChange={(e) => handleUpload(d.id, e.target.files?.[0])}
                  />
                </label>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-xl">
          {allDone
            ? "Tudo certo! Clique em concluir para enviar sua solicitação para análise."
            : "Envie todos os documentos para finalizar a solicitação."}
        </p>
        <button
          data-testid="finalize-submit"
          onClick={finalize}
          disabled={!allDone || finalizing}
          className="px-7 py-3 bg-[#0A0F14] text-white text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#002776] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {finalizing ? "Enviando..." : "Concluir e enviar para análise"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F0] bg-grain flex items-start justify-center p-6 py-10">
      <div className="w-full max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <Link to="/">
            <img src={LOGOS.monoBlack} alt="ABLEMP" className="h-12 w-auto" />
          </Link>
          <Link
            data-testid="signup-link-login"
            to="/login"
            className="text-sm text-slate-600 underline-offset-4 hover:underline"
          >
            Já tenho conta — entrar
          </Link>
        </div>

        <Stepper step={step} />

        <div className="bg-white border border-slate-200 p-8 md:p-12 rounded-md mt-6">
          {step === 1 && Step1}
          {step === 2 && Step2}
          {step === 3 && Step3}
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const Input = ({ testid, ...rest }) => (
  <input
    data-testid={testid}
    {...rest}
    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
  />
);

const StepBadge = ({ step, total, label }) => (
  <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
    Passo {step} de {total} — {label}
  </div>
);

const Stepper = ({ step }) => {
  const items = [
    { n: 1, label: "Tipo" },
    { n: 2, label: "Dados" },
    { n: 3, label: "Documentos" },
  ];
  return (
    <ol className="flex items-center gap-3">
      {items.map((it, i) => {
        const active = step === it.n;
        const done = step > it.n;
        return (
          <li key={it.n} className="flex items-center gap-3 flex-1">
            <div
              className={`h-8 w-8 grid place-items-center rounded-full text-xs font-semibold border ${
                active
                  ? "bg-[#0A0F14] text-white border-[#0A0F14]"
                  : done
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                  : "bg-white text-slate-500 border-slate-300"
              }`}
            >
              {done ? <CheckCircle2 size={14} /> : it.n}
            </div>
            <span
              className={`text-[11px] uppercase tracking-[0.22em] ${
                active ? "text-slate-900 font-semibold" : "text-slate-500"
              }`}
            >
              {it.label}
            </span>
            {i < items.length - 1 && (
              <span className="flex-1 h-px bg-slate-300" />
            )}
          </li>
        );
      })}
    </ol>
  );
};

const TypeCard = ({ icon: Icon, title, hint, onClick, accent, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="text-left p-6 bg-white border border-slate-200 hover:border-[#0A0F14] hover:shadow-md rounded-md transition-all"
  >
    <div
      className="h-12 w-12 grid place-items-center rounded-sm"
      style={{ background: accent + "14", color: accent }}
    >
      <Icon size={22} strokeWidth={1.5} />
    </div>
    <div className="mt-5 font-serif-display text-2xl leading-tight">
      {title}
    </div>
    <p className="mt-2 text-sm text-slate-600">{hint}</p>
  </button>
);
